import React, { useState, useEffect, useContext, useRef, forwardRef } from 'react';
import { AppContext } from '../../../util/AppContext';
import PropType from 'prop-types';
import { getConfigValue } from '../../../util/getConfigValue';
import { Chart as ChartJS, Title, Legend, ArcElement } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import {
	downloadDivChart,
	getTimePeriodLabel,
	utilizationReportConstants,
	standardizeChartNames,
	standardizeChartDataLabels,
	sortDoughnutChartData,
	getTimePeriodTile,
	getBaseDoughnutChartOptions,
	beforeDrawHandler,
	formatPercentLabel,
} from './utlizationChart';
import { getGenerationsByIndication } from '../../../API/UtilizationReportingDashboard';
import { useErrorBoundary } from 'react-error-boundary';
import ReportInfoTooltip from '../ReportInfoTooltip';
import Button from 'react-bootstrap/Button';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import Mustache from 'mustache';
import { formatNoDataMessage } from '../../../util/formatNoDataMessage';
import ChartSegmentTooltip from './ChartSegmentTooltip';
import { createColorPaletteRegistry } from '../ColorRegistry';

ChartJS.register(Title, Legend, ArcElement, ChartDataLabels, {
	id: 'centerTextPlugin',
	beforeDraw: beforeDrawHandler,
});

// Declared at module scope (not inside PdfGenerationsByIndicationChart) so it keeps a stable
// component identity across parent re-renders. Otherwise every drilldown click/tab toggle/collapse
// would redefine this component, forcing React to unmount+remount the canvas mid-hover and
// orphaning ChartSegmentTooltip's imperative show() with no matching hide().
const DoughnutChart = forwardRef(({ productData, columnSize, onSegmentClick, tooltipRef, centerText, isDrilldown, indicationTooltipText, appState }, chartRef) => {
	const chartData = {
		labels: productData.chartData.labels,
		datasets: productData.chartData.dataSets,
	};

	const baseDoughnutChartOptions = getBaseDoughnutChartOptions(appState);

	// Safety net: if this instance unmounts (e.g. the product list shrinks after a filter change)
	// while its tooltip is showing, make sure it doesn't outlive the chart that raised it.
	useEffect(() => {
		const tooltip = tooltipRef?.current;
		return () => {
			tooltip?.hide();
		};
	}, [tooltipRef]);

	const doughnutChartOptions = {
		type: 'doughnut',
		responsive: true,
		maintainAspectRatio: false,
		resizeDelay: 200,
		layout: baseDoughnutChartOptions.layout,
		plugins: {
			datalabels: baseDoughnutChartOptions.datalabels,
			legend: baseDoughnutChartOptions.legend,
			tooltip: {
				// Disabled in favor of ChartSegmentTooltip, which renders real HTML/JSX
				// instead of the canvas-drawn text the built-in tooltip is limited to.
				enabled: false,
			},
			title: baseDoughnutChartOptions.title,
			centerTextPlugin: {
				text: centerText,
			},
		},
		onClick: (event, activeElements) => {
			if (activeElements.length > 0) {
				// Dismiss immediately so stale hover content doesn't linger until the next mousemove.
				tooltipRef?.current?.hide();
				onSegmentClick(activeElements[0].index);
			}
		},
		onHover: (event, activeElements, chart) => {
			if (event.native?.target) {
				event.native.target.style.cursor = activeElements.length > 0 && !isDrilldown ? 'pointer' : 'default';
			}

			if (activeElements.length > 0 && event.native) {
				const index = activeElements[0].index;
				const value = chartData.datasets[0].data[index];
				const total = chartData.datasets[0].totals[0];
				const percentage = formatPercentLabel(value, total, appState);

				// Anchor on the arc's own midpoint - the same geometry Chart.js's built-in
				// tooltip positioner (mode: 'average') uses for a single active element - instead
				// of the raw cursor position, so the tooltip sits on the segment like the
				// automatic one does rather than jittering with the mouse inside it.
				const canvasRect = chart.canvas.getBoundingClientRect();
				const anchorPoint = activeElements[0].element.tooltipPosition(true);

				tooltipRef?.current?.show(
					canvasRect.left + anchorPoint.x,
					canvasRect.top + anchorPoint.y,
					<div className='chart-segment-tooltip-content'>
						<div className='d-flex align-items-center gap-1'>
							
							<strong>{chartData.labels[index]}</strong>
						</div>
						<div>
							<span
								className='chart-segment-tooltip-swatch'
								style={{ backgroundColor: chartData.datasets[0].backgroundColor?.[index] }}
							/>
							{value} <span className='color-grey-d1'>({percentage})</span>
							{!isDrilldown && (
								<>
									<hr></hr>
									{indicationTooltipText}
								</>
							)}
						</div>
					</div>,
					`${productData.name}-${index}`,
				);
			} else {
				tooltipRef?.current?.hide();
			}
		},
	};

	return (
		// Native mouseleave is a reliable safety net independent of Chart.js's own onHover([])
		// firing, which isn't guaranteed on every browser/fast-pointer-movement scenario.
		<div className={`col-${columnSize} chart-canvas-wrapper mb-2`} onMouseLeave={() => tooltipRef?.current?.hide()}>
			<Doughnut ref={chartRef} options={doughnutChartOptions} data={chartData} />
		</div>
	);
});

DoughnutChart.displayName = 'DoughnutChart';

DoughnutChart.propTypes = {
	productData: PropType.shape({
		name: PropType.string,
		chartData: PropType.shape({
			labels: PropType.arrayOf(PropType.string),
			dataSets: PropType.arrayOf(PropType.object),
		}),
	}).isRequired,
	columnSize: PropType.oneOfType([PropType.string, PropType.number]).isRequired,
	onSegmentClick: PropType.func.isRequired,
	tooltipRef: PropType.shape({ current: PropType.any }),
	centerText: PropType.string,
	isDrilldown: PropType.bool,
	indicationTooltipText: PropType.node,
	appState: PropType.object,
};

PdfGenerationsByIndicationChart.propTypes = {
	filterState: PropType.object,
	colorRegistry: PropType.object,
	breakDownBy: PropType.number.isRequired,
	configKey: PropType.string.isRequired,
};

function PdfGenerationsByIndicationChart(props) {
	const { filterState, colorRegistry, breakDownBy, configKey } = props;

	const { showBoundary } = useErrorBoundary();
	const [appState] = useContext(AppContext);
	const doughnutChartRefs = useRef([]);
	const [data, setData] = useState({});
	const [doughnutChartData, setDoughnutChartData] = useState({});
	const [isLoading, setIsLoading] = useState(null);
	const [isCollapsed, setIsCollapsed] = useState(false);
	const [activeButton, setActiveButton] = useState(utilizationReportConstants.current);
	const tooltipTarget = useRef(null);
	const chartEnabled = JSON.parse(getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.[configKey]?.Enabled, appState)?.Value === 'true') || false;
	const [drilldownIndication, setDrilldownIndication] = useState(null);
	const [noData, setNoData] = useState(null);
	const [chartColorRegistry] = useState(() => createColorPaletteRegistry(
		JSON.parse(getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.ColorPalette, appState)?.Value) || [],
		{ storageKey: `utilizationReportingDashboard:${configKey}:colorpalette` }
	));
	const segmentTooltipRef = useRef(null);
	const chartsAreaId = `${configKey}-ChartsArea`;
	const indicationTooltipText = getConfigValue(
		appState?.config?.UtilizationDashboard?.Charts?.[configKey]?.HelperText?.IndicationToolTip,
		appState,
	)?.Value;

	const getData = async () => {
		if (!filterState?.selectedProductIds) {
			return;
		}

		setIsLoading(true);
		await getGenerationsByIndication({
			timeFilter: filterState.timePeriodId,
			productIds: filterState.selectedProductIds,
			geography: filterState.selectedGeography,
			geographyType: filterState.selectedGeographyType,
			breakDownBy: breakDownBy,
			includePriorPeriod: true,
		})
			.then((responseJson) => {
				setData(responseJson);
				setChartData(responseJson.currentPeriod);
			})
			.catch((err) => {
				showBoundary(err);
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const toggleChartTab = (activeTab, selectedData) => {
		if (drilldownIndication === null) {
			setChartData(selectedData);
		} else {
			const modifiedPeriod = selectedData.map((product) => ({
				...product,
				chartData: product.drillDownData?.[drilldownIndication.index]?.chartData ?? product.chartData,
			}));
			setChartData(modifiedPeriod);
		}
		setActiveButton(activeTab);
	};

	const handleSegmentClick = (segmentIndex) => {
		if (drilldownIndication !== null) {
			return;
		}
		const periodData = activeButton === utilizationReportConstants.prior ? data.priorPeriod : data.currentPeriod;
		const firstProduct = periodData.find((p) => p.drillDownData?.[segmentIndex]);
		if (!firstProduct) {
			return;
		}
		const indicationLabel = firstProduct.drillDownData[segmentIndex].name;
		const modifiedPeriod = periodData.map((product) => ({
			...product,
			chartData: product.drillDownData?.[segmentIndex]?.chartData ?? product.chartData,
		}));
		setChartData(modifiedPeriod);
		setDrilldownIndication({ index: segmentIndex, label: indicationLabel });
	};

	const handleBackFromDrilldown = () => {
		const periodData = activeButton === utilizationReportConstants.prior ? data.priorPeriod : data.currentPeriod;
		setChartData(periodData);
		setDrilldownIndication(null);
	};

	const setChartData = (selectedData) => {
		chartColorRegistry.reset();
		if (filterState.allProducts) {
			return;
		}
		let filteredDataSet = selectedData.filter((p) => p.name !== utilizationReportConstants.allProducts);

		setNoData(filteredDataSet.filter((p) => p.chartData.labels.includes(utilizationReportConstants.no_data)));
		filteredDataSet = filteredDataSet.filter((p) => !p.chartData.labels.includes(utilizationReportConstants.no_data));

		let standardizedData = standardizeChartNames(filteredDataSet, appState);
		standardizedData = standardizeChartDataLabels(standardizedData, appState);

		const data = standardizedData.map((item) => {
			const sortedChartData = sortDoughnutChartData(item.chartData);
			return {
				...item,
				chartData: {
					...sortedChartData,
					dataSets: sortedChartData.dataSets.map((ds) => addColorPalette(ds, sortedChartData.labels)),
				},
			};
		});

		setDoughnutChartData(data);
	};

	const addColorPalette = (selectedDataSet, labels = []) => {
		const assignedColorPallet = labels.map((key) => chartColorRegistry.getColorForKey(key));
		return {
			...selectedDataSet,
			backgroundColor: assignedColorPallet,
		};
	};

	useEffect(() => {
		if (chartEnabled && !filterState.allProducts && appState?.config) {
			getData();
			setActiveButton(utilizationReportConstants.current);
			setDrilldownIndication(null);
		}
	}, [filterState.selectedProductIds, filterState.allProducts, filterState.timePeriodId, filterState.selectedGeography]);

	let content;
	if (isLoading === null || isLoading) {
		content = <div className='p-5'>{getConfigValue(appState.config.Common?.Loading, appState)?.Value}</div>;
	} else {
		content = (
			<>
				<ChartSegmentTooltip ref={segmentTooltipRef} />
				{drilldownIndication !== null && (
					<div className='drilldown-breadcrumb d-flex align-items-center'>
						<span className='drilldown-breadcrumb-pill'>
							{drilldownIndication.label}
							<span className='drilldown-breadcrumb-separator'>&rsaquo;</span>
							<span className='drilldown-breadcrumb-current'>
								{getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.[configKey]?.HelperText?.DrillDownLevelLabel, appState)?.Value}
							</span>
						</span>
						<Button className='reportTextButton' onClick={handleBackFromDrilldown}>
							&larr; {getConfigValue(appState.config?.Common?.Back, appState)?.Value || 'Back'}
						</Button>
					</div>
				)}
				<div id={chartsAreaId}>
					<div className='p-4 chart-label'>
						<div>{getTimePeriodLabel(filterState.timePeriodId, activeButton, false)}</div>
						<div>
							{filterState.allProducts
								? Mustache.render(
										getConfigValue(appState?.config?.UtilizationDashboard?.Filters?.Product?.AllProducts?.Label, appState)?.Value || null,
										{ count: appState?.sellSheetProductDetails?.length },
									)
								: filterState.selectedProductIds
										?.map((id) => {
											const product = appState?.sellSheetProductDetails.find((p) => p.clientProductId === id);
											return product.name || `Unknow (${id})`;
										})
										.join(', ')}
						</div>
						<div>
							{filterState.selectedGeography
								? filterState.selectedGeographyLabel
								: getConfigValue(appState?.config?.UtilizationDashboard?.Filters?.Product?.AllGeographies?.Label, appState)?.Value || null}
						</div>
						{drilldownIndication === null && (
							<div className='chart-helper-text'>
								{getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.[configKey]?.HelperText?.SelectIndication, appState)?.Value}
							</div>
						)}
					</div>
					<div className='container-fluid'>
						{Array.from({ length: Math.ceil(doughnutChartData.length / 2) }, (_, i) => {
							return (
								<div className='row mb-2 justify-content-center align-items-center' key={i}>
									{doughnutChartData.slice(i * 2, (i + 1) * 2).map((product, columnIndex) => {
										const refIndex = i * 2 + columnIndex;
										if (!doughnutChartRefs?.current[refIndex]) {
											doughnutChartRefs.current[refIndex] = React.createRef();
										}
										return (
											<DoughnutChart
												ref={doughnutChartRefs.current[refIndex]}
												columnSize={6}
												productData={product}
												onSegmentClick={handleSegmentClick}
												tooltipRef={segmentTooltipRef}
												centerText={drilldownIndication === null ? product.name : drilldownIndication.label}
												isDrilldown={drilldownIndication !== null}
												indicationTooltipText={indicationTooltipText}
												appState={appState}
												key={refIndex}
											/>
										);
									})}
								</div>
							);
						})}
					</div>
					<div className='p-4 chart-label'>{formatNoDataMessage(noData)}</div>
				</div>
				<div className='reportButtons-container'>
					<Button
						className='reportTextButton'
						onClick={() =>
							downloadDivChart(
								chartsAreaId,
								getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.[configKey]?.Title, appState)?.Value || null,
							)
						}
					>
						{getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.Buttons?.Download?.Text, appState)?.Value || null}{' '}
						<i className={getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.Buttons?.Download?.Icon, appState)?.Value || null}></i>
					</Button>
				</div>
				<div className='reportTabButton-container'>
					<Button
						className={`tab-button ${activeButton === utilizationReportConstants.current ? 'active' : 'inactive'}`}
						onClick={() => {
							toggleChartTab(utilizationReportConstants.current, data.currentPeriod);
						}}
					>
						{getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.Buttons?.CurrentSelection?.Text, appState)?.Value || null} (
						{getTimePeriodTile(filterState.timePeriodId, filterState.timePeriodTitle, false)})
					</Button>
					<Button
						className={`tab-button ${activeButton === utilizationReportConstants.prior ? 'active' : 'inactive'}`}
						onClick={() => {
							toggleChartTab(utilizationReportConstants.prior, data.priorPeriod);
						}}
					>
						{getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.Buttons?.PriorTimePeriod?.Text, appState)?.Value || null}
					</Button>
				</div>
			</>
		);
	}

	return (
		<>
			{chartEnabled && !filterState.allProducts && (
				<div className='chart-panel'>
					<div className='chart-header'>
						<Button
							className={`chart-header-button ${isCollapsed ? 'collapsed' : ''}`}
							onClick={() => {
								setIsCollapsed(!isCollapsed);
							}}
						>
							{getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.[configKey]?.Title, appState)?.Value ||
								'PDF Generations by Indication'}
							<ReportInfoTooltip
								ref={tooltipTarget}
								text={getConfigValue(appState?.config?.UtilizationDashboard?.Charts?.[configKey]?.ToolTip, appState)?.Value}
							/>
						</Button>
					</div>
					<div className={`chart-container ${isCollapsed ? 'collapsed' : ''}`}>{content}</div>
				</div>
			)}
		</>
	);
}

export default PdfGenerationsByIndicationChart;
